import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleLogin, GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login'
import { Helmet } from 'react-helmet-async'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { ApolloError } from '@apollo/client'
import {
  CCol,
  CContainer,
  CForm,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CLink,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { ReactComponent as Google } from '@assets/images/google.svg'
import AppLogo from '@components/atoms/AppLogo'
import PasswordInput from '@components/molecules/PasswordInput'
import { middleBlue } from '@constants/colors'
import { useAuth } from '@contexts/authContext'
import {
  LoginSource,
  useLoginUserWithGoogleMutation,
  useLoginUserWithPasswordMutation,
  useMeQuery,
  useVerify2FaCodeAndLoginMutation,
} from '@graphQLGenerated'
import { analytics } from '@utils/segment'

import LeftSide from '../containers/LeftSide'

import '../styles/loginStyles.scss'

const Login = () => {
  const [generalError, setGeneralError] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [show2FAView, setShow2FAView] = useState(false)
  const [twoFactorCode, setTwoFactorCode] = useState<string>()

  const loginDone = useRef(false)

  const [loginWithPassword, { data: loginData, loading: loginLoading }] =
    useLoginUserWithPasswordMutation()
  const [loginWithGoogle, { data: googleData, loading: googleLoading }] =
    useLoginUserWithGoogleMutation()
  const [verify2FACodeAndLogin, { data: verify2FAData, loading: verify2FALoading }] =
    useVerify2FaCodeAndLoginMutation()
  const { data: meData, refetch: refetchMe } = useMeQuery()

  const auth = useAuth()
  const navigate = useNavigate()

  const schema = yup.object().shape({
    email: yup.string().email('Email provided is incorrect').required('Required field'),
    password: yup.string().required('Required field'),
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<{
    email: string
    password: string
  }>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  useEffect(() => {
    if (auth.token) {
      navigate('/', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  useEffect(() => {
    const token =
      (!loginData?.loginUserWithPassword.is2FAEnabled && loginData?.loginUserWithPassword.token) ||
      googleData?.loginUserWithGoogle.token ||
      verify2FAData?.verify2FACodeAndLogin.token

    if (!token) return

    if (!meData?.me) {
      refetchMe()
    }

    localStorage.setItem('token', token)
    auth.signin(token, () => {
      if (meData && meData?.me && !loginDone.current) {
        loginDone.current = true

        analytics.identify(meData.me.id as string)

        if (meData.me.organization) {
          const navigateToHome = () => {
            navigate('/', { replace: true })
          }
          // if (!meData.me.twoFactorEnabled && shouldShowActivate2FAModal()) {
          //   activate2FAModal.setContent({
          //     onClickActivate: () => {
          //       navigate(`/profile/?enable-2fa=true`)
          //     },
          //     onClose: navigateToHome,
          //   })
          // } else {
          //   navigateToHome()
          // }
          navigateToHome()
        } else {
          navigate('/onboarding', { replace: true })
        }
      }
    })
  }, [auth, loginData, navigate, meData, refetchMe, googleData, verify2FAData])

  useEffect(() => {
    if (loginData?.loginUserWithPassword.is2FAEnabled) {
      setShow2FAView(true)
    }
  }, [loginData])

  const handleOnSubmit = useCallback(async () => {
    const { email, password } = getValues()
    if (!loginLoading && !googleLoading) {
      try {
        setGeneralError('')
        const data = await loginWithPassword({
          variables: {
            email,
            password,
            rememberMe,
            loginSource: LoginSource.Dashboard,
          },
        })
        if (!data.data?.loginUserWithPassword.token) {
          setShow2FAView(true)
        }
      } catch (e) {
        if (e instanceof ApolloError) {
          setGeneralError(e.message)
        }
      }
    }
  }, [getValues, googleLoading, loginLoading, loginWithPassword, rememberMe])

  const responseGoogle = useCallback(
    async (response: GoogleLoginResponse | GoogleLoginResponseOffline) => {
      if (response?.code) {
        loginWithGoogle({
          variables: {
            authCode: response.code,
            redirectUri: 'postmessage',
            rememberMe,
            loginSource: LoginSource.Dashboard,
          },
        })
      }
    },
    [loginWithGoogle, rememberMe],
  )

  const handleVerify2FACode = useCallback(async () => {
    if (!verify2FALoading && loginData?.loginUserWithPassword.token && twoFactorCode) {
      try {
        setGeneralError('')
        await verify2FACodeAndLogin({
          variables: {
            code: twoFactorCode,
            token: loginData?.loginUserWithPassword.token,
            rememberMe,
          },
        })
      } catch (e) {
        if (e instanceof ApolloError) {
          setGeneralError(e.message)
        }
      }
    }
  }, [
    loginData?.loginUserWithPassword.token,
    rememberMe,
    twoFactorCode,
    verify2FACodeAndLogin,
    verify2FALoading,
  ])

  const verify2FACustomErrors = useMemo(() => {
    if (generalError.includes('Too many requests')) {
      return 'Too many attempts. Please try again in 10 minutes.'
    }
    if (generalError.includes('Invalid two-factor code')) {
      return 'Invalid code. Please try again.'
    }
    return generalError
  }, [generalError])

  const loginView = useMemo(
    () => (
      <div className="loginContainerRight">
        <div className="loginRightInner">
          <AppLogo className="loginHiddenLogo" withoutText />
          <h4 className="mb-3">Welcome back!</h4>
          <CForm onSubmit={handleSubmit(handleOnSubmit)} className="loginFormContainer">
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <div className="FormInputTextContainer">
                    <CFormLabel className="FormInputTextLabel body-small-font">Email</CFormLabel>
                    <CFormInput
                      onChange={field.onChange}
                      value={field.value || ''}
                      className="body-small-font"
                      autoComplete="email"
                      invalid={!!errors.email}
                    />
                    {errors.email && (
                      <div className="caption-font generalTextError">{errors.email?.message}</div>
                    )}
                  </div>
                )
              }}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="FormInputTextContainer loginCustomInputGroup">
                  <CFormLabel className="FormInputTextLabel body-small-font">Password</CFormLabel>
                  <PasswordInput
                    onChange={field.onChange}
                    value={field.value || ''}
                    className="body-small-font"
                    autoComplete="current-password"
                    invalid={!!errors.password}
                  />
                  {errors.password && (
                    <div className="caption-font generalTextError">{errors.password?.message}</div>
                  )}
                </div>
              )}
            />
            <div
              className="text-button loginForgot"
              onClick={() => {
                navigate('/reset-password')
              }}
            >
              Forgot Password?
            </div>
            <CFormCheck
              id="remember_me"
              className="mt-1 mb-3"
              label="Remember me"
              checked={rememberMe}
              onChange={() => {
                setRememberMe(!rememberMe)
              }}
            />
            {generalError?.length ? (
              <div className="caption-font generalTextError mb-2">{generalError}</div>
            ) : null}
            <CLoadingButton color="primary" type="submit" loading={loginLoading || googleLoading}>
              Login
            </CLoadingButton>
            <div className="button-font mt-3 w-100 text-center">
              No account?{' '}
              <CLink className="text-button" style={{ textDecoration: 'none' }} href="/register">
                Sign-up
              </CLink>
            </div>
            <br />
            <CRow>
              <div className="loginHrContainer">
                <hr className="loginHr" />
                <span className="overline-font loginHrText">or</span>
                <hr className="loginHr" />
              </div>
            </CRow>
            <br />
            <GoogleLogin
              render={(props) => (
                <div className="loginWithGoogle clickable" onClick={props.onClick}>
                  <span>
                    <Google style={{ marginRight: 10 }} />
                  </span>
                  Log-in with Google
                </div>
              )}
              className="box-shadow-none"
              clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID as string}
              onSuccess={responseGoogle}
              onFailure={responseGoogle}
              accessType="offline"
              responseType="code"
              cookiePolicy="single_host_origin"
              hostedDomain="umore.app"
            />
          </CForm>
        </div>
      </div>
    ),
    [
      handleSubmit,
      handleOnSubmit,
      control,
      rememberMe,
      generalError,
      loginLoading,
      googleLoading,
      responseGoogle,
      errors.email,
      errors.password,
      navigate,
    ],
  )

  const twoFactorView = useMemo(
    () => (
      <CContainer className="loginContainerRight">
        <CRow className="ms-5">
          <CCol>
            <h3>Authentication Required</h3>
            <p className="subheader-font">
              Enter the code from your two-factor authentication app.
              <br />
              If you have lost your device, please contact our team for support.
            </p>
            <br /> <br />
            <div className="FormInputTextContainer">
              <CFormLabel className="FormInputTextLabel body-small-font">
                Enter verification code
              </CFormLabel>
              <CFormInput
                disabled={verify2FALoading}
                className="body-small-font"
                onChange={(e) => {
                  setGeneralError('')
                  setTwoFactorCode(e.target.value)
                }}
                value={twoFactorCode || ''}
                placeholder="Enter code here"
                invalid={!!verify2FACustomErrors}
              />
              {verify2FACustomErrors && (
                <div className="caption-font generalTextError">{verify2FACustomErrors}</div>
              )}
            </div>
            <CLoadingButton
              disabled={!twoFactorCode || twoFactorCode?.length < 6 || verify2FALoading}
              color="primary"
              className="px-4 mt-3"
              onClick={handleVerify2FACode}
              loading={verify2FALoading}
            >
              Verify code
            </CLoadingButton>
            <div
              style={{ color: middleBlue }}
              className="mt-4 text-center clickable"
              onClick={() => {
                setShow2FAView(false)
                setTwoFactorCode(undefined)
              }}
            >
              Return to basic login
            </div>
          </CCol>
        </CRow>
      </CContainer>
    ),
    [handleVerify2FACode, twoFactorCode, verify2FACustomErrors, verify2FALoading],
  )

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-stretch">
      <Helmet prioritizeSeoTags>
        <title>uMore</title>
      </Helmet>
      <LeftSide />
      {!show2FAView ? loginView : twoFactorView}
    </div>
  )
}

export default Login
